/**
 * Steuerschlüssel — the bridge between a booking and the U30 form.
 *
 * Each code says how one line flows into the UVA. `effects` is the whole
 * truth: the UVA report is nothing but a fold of every booking's effects.
 *
 * Two cases are easy to get wrong and are therefore spelled out:
 *
 *  - **Innergemeinschaftliche sonstige Leistungen (B2B)** move the place of
 *    supply to the customer's country (§ 3a Abs 6 UStG). They are *not*
 *    steuerbar in Austria, so they appear in **no** UVA Kennzahl at all — only
 *    in the Zusammenfassende Meldung. Hence `effects: []`.
 *  - **Reverse-charge purchases and ig Erwerbe** book the tax we owe and the
 *    input VAT we may reclaim in the same breath. Both effects are listed, so
 *    they net to zero for a fully deductible business — exactly what the
 *    Finanzamt expects to see on the form.
 */
import { formatDate } from '../../dates.js';
const RC_NOTE = 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)';
/**
 * § 10 Abs 1a UStG iVm Anlage 3 (BGBl I 37/2026): 4,9 % auf ausgewählte
 * Grundnahrungsmittel ab dem 1. Juli 2026. Der schiefe Satz ist unionsrechtlich
 * bedingt — ein dritter ermäßigter Satz ist nach der MwStSystRL nur unter 5 %
 * zulässig. Maßgeblich ist der Tag der Lieferung, nicht der Rechnungstag.
 */
const FOOD_RATE_START = '2026-07-01';
/**
 * The Austrian rate codes are `any`, not `domestic`: § 3a Abs 7 UStG keeps a
 * B2C service taxable in Austria even when the customer sits abroad, so a
 * 20 % invoice to a German private person is not just allowed, it is required.
 * Restricting these to domestic recipients would block every B2C export.
 */
const SALES_CODES = [
    {
        key: 'at-20',
        side: 'sales',
        kind: 'domestic',
        label: 'Österreich 20 % Normalsteuersatz',
        shortLabel: '20 %',
        rate: 0.2,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'any',
        effects: [
            { kz: '000', basis: 'net', sign: 1 },
            { kz: '022', basis: 'net', sign: 1 },
        ],
    },
    {
        key: 'at-10',
        side: 'sales',
        kind: 'domestic',
        label: 'Österreich 10 % ermäßigt',
        shortLabel: '10 %',
        rate: 0.1,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'any',
        effects: [
            { kz: '000', basis: 'net', sign: 1 },
            { kz: '029', basis: 'net', sign: 1 },
        ],
    },
    {
        key: 'at-13',
        side: 'sales',
        kind: 'domestic',
        label: 'Österreich 13 % ermäßigt',
        shortLabel: '13 %',
        rate: 0.13,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'any',
        effects: [
            { kz: '000', basis: 'net', sign: 1 },
            { kz: '006', basis: 'net', sign: 1 },
        ],
    },
    {
        key: 'at-4-9',
        side: 'sales',
        kind: 'domestic',
        label: 'Österreich 4,9 % Grundnahrungsmittel (§ 10 Abs 1a UStG)',
        shortLabel: '4,9 %',
        rate: 0.049,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'any',
        validFrom: FOOD_RATE_START,
        effects: [
            { kz: '000', basis: 'net', sign: 1 },
            { kz: '124', basis: 'net', sign: 1 },
        ],
    },
    {
        key: 'at-kleinunternehmer',
        side: 'sales',
        kind: 'small_business',
        label: 'Kleinunternehmer — unecht steuerbefreit (§ 6 Abs 1 Z 27 UStG)',
        shortLabel: 'KU 0 %',
        rate: 0,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'any',
        invoiceNote: 'Umsatzsteuerbefreit — Kleinunternehmer gemäß § 6 Abs 1 Z 27 UStG',
        effects: [
            { kz: '000', basis: 'net', sign: 1 },
            { kz: '016', basis: 'net', sign: 1 },
        ],
    },
    {
        key: 'eu-service-b2b',
        side: 'sales',
        kind: 'ic_supply_service',
        label: 'EU B2B — sonstige Leistung, Reverse Charge (Art 196 MwStSystRL)',
        shortLabel: 'EU RC',
        rate: 0,
        reverseCharge: true,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'eu',
        requiresCounterpartyVatId: true,
        invoiceNote: RC_NOTE,
        ecSalesList: 'services',
        // Leistungsort ist im Empfängerstaat — in Österreich nicht steuerbar.
        effects: [],
    },
    {
        key: 'eu-supply-goods',
        side: 'sales',
        kind: 'ic_supply_goods',
        label: 'Innergemeinschaftliche Lieferung (Art 6 Abs 1 UStG)',
        shortLabel: 'IG-Lieferung',
        rate: 0,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'eu',
        requiresCounterpartyVatId: true,
        invoiceNote: 'Steuerfreie innergemeinschaftliche Lieferung gemäß Art 6 Abs 1 UStG',
        ecSalesList: 'goods',
        effects: [
            { kz: '000', basis: 'net', sign: 1 },
            { kz: '017', basis: 'net', sign: 1 },
        ],
    },
    {
        key: 'export-goods',
        side: 'sales',
        kind: 'export',
        label: 'Ausfuhrlieferung Drittland (§ 6 Abs 1 Z 1 iVm § 7 UStG)',
        shortLabel: 'Ausfuhr',
        rate: 0,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'third',
        invoiceNote: 'Steuerfreie Ausfuhrlieferung gemäß § 6 Abs 1 Z 1 iVm § 7 UStG',
        effects: [
            { kz: '000', basis: 'net', sign: 1 },
            { kz: '011', basis: 'net', sign: 1 },
        ],
    },
    {
        key: 'third-country-service',
        side: 'sales',
        kind: 'third_country_service',
        label: 'Drittland — sonstige Leistung, nicht steuerbar (§ 3a Abs 6 UStG)',
        shortLabel: 'Drittland',
        rate: 0,
        reverseCharge: true,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'third',
        invoiceNote: 'Nicht steuerbare sonstige Leistung — Leistungsort im Empfängerland (§ 3a Abs 6 UStG)',
        effects: [],
    },
    {
        key: 'at-rc-construction',
        side: 'sales',
        kind: 'reverse_charge_out',
        label: 'Inland Bauleistung — Übergang der Steuerschuld (§ 19 Abs 1a UStG)',
        shortLabel: 'RC Bau',
        rate: 0,
        reverseCharge: true,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'domestic',
        invoiceNote: RC_NOTE,
        effects: [
            { kz: '000', basis: 'net', sign: 1 },
            { kz: '021', basis: 'net', sign: 1 },
        ],
    },
    {
        key: 'at-exempt-other',
        side: 'sales',
        kind: 'exempt_false',
        label: 'Übrige steuerfreie Umsätze ohne Vorsteuerabzug (§ 6 Abs 1 UStG)',
        shortLabel: '0 % befreit',
        rate: 0,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'any',
        effects: [
            { kz: '000', basis: 'net', sign: 1 },
            { kz: '020', basis: 'net', sign: 1 },
        ],
    },
    {
        key: 'sales-no-vat',
        side: 'sales',
        kind: 'no_vat',
        label: 'Nicht umsatzsteuerbar / durchlaufender Posten',
        shortLabel: 'o. USt',
        rate: 0,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'any',
        effects: [],
    },
];
const PURCHASE_CODES = [
    {
        key: 'vst-20',
        side: 'purchase',
        kind: 'domestic',
        label: 'Vorsteuer 20 %',
        shortLabel: '20 %',
        rate: 0.2,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: true,
        countryScope: 'domestic',
        effects: [{ kz: '060', basis: 'vat', sign: 1 }],
    },
    {
        key: 'vst-10',
        side: 'purchase',
        kind: 'domestic',
        label: 'Vorsteuer 10 %',
        shortLabel: '10 %',
        rate: 0.1,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: true,
        countryScope: 'domestic',
        effects: [{ kz: '060', basis: 'vat', sign: 1 }],
    },
    {
        key: 'vst-13',
        side: 'purchase',
        kind: 'domestic',
        label: 'Vorsteuer 13 %',
        shortLabel: '13 %',
        rate: 0.13,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: true,
        countryScope: 'domestic',
        effects: [{ kz: '060', basis: 'vat', sign: 1 }],
    },
    {
        key: 'vst-4-9',
        side: 'purchase',
        kind: 'domestic',
        label: 'Vorsteuer 4,9 % (Grundnahrungsmittel)',
        shortLabel: '4,9 %',
        rate: 0.049,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: true,
        countryScope: 'domestic',
        validFrom: FOOD_RATE_START,
        effects: [{ kz: '060', basis: 'vat', sign: 1 }],
    },
    {
        key: 'vst-0',
        side: 'purchase',
        kind: 'no_vat',
        label: 'Ohne Umsatzsteuer (z.B. Kleinunternehmer, steuerbefreit)',
        shortLabel: '0 %',
        rate: 0,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'any',
        effects: [],
    },
    {
        key: 'vst-none',
        side: 'purchase',
        kind: 'exempt_false',
        label: 'Kein Vorsteuerabzug (§ 12 Abs 3 UStG)',
        shortLabel: 'kein VSt',
        rate: 0.2,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'any',
        effects: [],
    },
    {
        key: 'ig-erwerb-20',
        side: 'purchase',
        kind: 'ic_acquisition',
        label: 'Innergemeinschaftlicher Erwerb 20 %',
        shortLabel: 'IG-Erwerb 20 %',
        rate: 0.2,
        reverseCharge: false,
        selfAssessed: true,
        inputVatDeductible: true,
        countryScope: 'eu',
        requiresCounterpartyVatId: true,
        effects: [
            { kz: '070', basis: 'net', sign: 1 },
            { kz: '072', basis: 'net', sign: 1 },
            { kz: '065', basis: 'vat', sign: 1 },
        ],
    },
    {
        key: 'ig-erwerb-10',
        side: 'purchase',
        kind: 'ic_acquisition',
        label: 'Innergemeinschaftlicher Erwerb 10 %',
        shortLabel: 'IG-Erwerb 10 %',
        rate: 0.1,
        reverseCharge: false,
        selfAssessed: true,
        inputVatDeductible: true,
        countryScope: 'eu',
        requiresCounterpartyVatId: true,
        effects: [
            { kz: '070', basis: 'net', sign: 1 },
            { kz: '073', basis: 'net', sign: 1 },
            { kz: '065', basis: 'vat', sign: 1 },
        ],
    },
    {
        key: 'ig-erwerb-4-9',
        side: 'purchase',
        kind: 'ic_acquisition',
        label: 'Innergemeinschaftlicher Erwerb 4,9 % (Grundnahrungsmittel)',
        shortLabel: 'IG-Erwerb 4,9 %',
        rate: 0.049,
        reverseCharge: false,
        selfAssessed: true,
        inputVatDeductible: true,
        countryScope: 'eu',
        requiresCounterpartyVatId: true,
        validFrom: FOOD_RATE_START,
        effects: [
            { kz: '070', basis: 'net', sign: 1 },
            { kz: '125', basis: 'net', sign: 1 },
            { kz: '065', basis: 'vat', sign: 1 },
        ],
    },
    {
        key: 'rc-service-20',
        side: 'purchase',
        kind: 'reverse_charge_in',
        label: 'Reverse Charge Leistungsbezug 20 % (§ 19 Abs 1 zweiter Satz UStG)',
        shortLabel: 'RC 20 %',
        rate: 0.2,
        reverseCharge: false,
        selfAssessed: true,
        inputVatDeductible: true,
        countryScope: 'any',
        effects: [
            { kz: '057', basis: 'vat', sign: 1 },
            { kz: '066', basis: 'vat', sign: 1 },
        ],
    },
    {
        key: 'rc-service-10',
        side: 'purchase',
        kind: 'reverse_charge_in',
        label: 'Reverse Charge Leistungsbezug 10 % (§ 19 Abs 1 zweiter Satz UStG)',
        shortLabel: 'RC 10 %',
        rate: 0.1,
        reverseCharge: false,
        selfAssessed: true,
        inputVatDeductible: true,
        countryScope: 'any',
        effects: [
            { kz: '057', basis: 'vat', sign: 1 },
            { kz: '066', basis: 'vat', sign: 1 },
        ],
    },
    {
        key: 'rc-construction-20',
        side: 'purchase',
        kind: 'reverse_charge_in',
        label: 'Reverse Charge Bauleistung 20 % (§ 19 Abs 1a UStG)',
        shortLabel: 'RC Bau',
        rate: 0.2,
        reverseCharge: false,
        selfAssessed: true,
        inputVatDeductible: true,
        countryScope: 'domestic',
        effects: [
            { kz: '048', basis: 'vat', sign: 1 },
            { kz: '082', basis: 'vat', sign: 1 },
        ],
    },
    {
        key: 'import-vat-paid',
        side: 'purchase',
        kind: 'import_vat',
        label: 'Einfuhrumsatzsteuer entrichtet (§ 12 Abs 1 Z 2 lit a UStG)',
        shortLabel: 'EUSt',
        rate: 0.2,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: true,
        countryScope: 'third',
        effects: [{ kz: '061', basis: 'vat', sign: 1 }],
    },
    {
        key: 'import-vat-deferred',
        side: 'purchase',
        kind: 'import_vat',
        label: 'Einfuhrumsatzsteuer auf Abgabenkonto verbucht (§ 12 Abs 1 Z 2 lit b UStG)',
        shortLabel: 'EUSt Konto',
        rate: 0.2,
        reverseCharge: false,
        selfAssessed: true,
        inputVatDeductible: true,
        countryScope: 'third',
        effects: [{ kz: '083', basis: 'vat', sign: 1 }],
    },
    {
        key: 'purchase-private',
        side: 'purchase',
        kind: 'no_vat',
        label: 'Privatentnahme / nicht betrieblich',
        shortLabel: 'privat',
        rate: 0,
        reverseCharge: false,
        selfAssessed: false,
        inputVatDeductible: false,
        countryScope: 'any',
        effects: [],
    },
];
/**
 * Eigenverbrauch. Nobody books these on a Beleg — there is none. They are the
 * counter-entry the posting engine derives when a rule takes back a deduction
 * that was already claimed, which is why they carry `systemOnly`.
 *
 * The U30 keeps the Eigenverbrauch out of KZ 000 ("ohne Eigenverbrauch") and
 * gives it KZ 001 of its own; the tax itself follows the ordinary rate field.
 */
const OWN_USE_CODES = [
    {
        key: 'ev-20',
        side: 'sales',
        kind: 'own_use',
        label: 'Aufwandseigenverbrauch 20 % (§ 1 Abs 1 Z 2 lit a UStG)',
        shortLabel: 'Eigenverbrauch 20 %',
        rate: 0.2,
        reverseCharge: false,
        selfAssessed: true,
        inputVatDeductible: false,
        countryScope: 'domestic',
        systemOnly: true,
        effects: [
            { kz: '001', basis: 'net', sign: 1 },
            { kz: '022', basis: 'net', sign: 1 },
        ],
    },
];
export const AT_TAX_CODES = [
    ...SALES_CODES,
    ...PURCHASE_CODES,
    ...OWN_USE_CODES,
];
/**
 * A Kleinunternehmer may not show VAT on outgoing invoices and gets no input
 * VAT back, so the picker shrinks to the codes that stay legal.
 */
export const KLEINUNTERNEHMER_SALES_CODES = [
    'at-kleinunternehmer',
    'eu-service-b2b',
    'third-country-service',
    'sales-no-vat',
];
export const KLEINUNTERNEHMER_PURCHASE_CODES = [
    'vst-0',
    'vst-none',
    'purchase-private',
    'rc-service-20',
    'rc-service-10',
    'ig-erwerb-20',
    'ig-erwerb-10',
];
/**
 * The codes a calendar year knows. A code that only starts mid-year is already
 * part of that year — `validFrom` then decides per document whether a concrete
 * supply may use it (see `isTaxCodeValidOn`). Earlier years never see it, so a
 * closed year cannot grow a rate it never had.
 */
export function taxCodes(year) {
    return AT_TAX_CODES.filter((code) => {
        if (code.validFrom && Number(code.validFrom.slice(0, 4)) > year)
            return false;
        if (code.validUntil && Number(code.validUntil.slice(0, 4)) < year)
            return false;
        return true;
    });
}
/**
 * Whether a code may be used for a supply on `date`. The relevant day is the
 * Leistungsdatum — § 19 Abs 2 UStG ties the Steuerschuld to the supply, not to
 * the invoice.
 */
export function isTaxCodeValidOn(code, date) {
    if (code.validFrom && date < code.validFrom)
        return false;
    if (code.validUntil && date > code.validUntil)
        return false;
    return true;
}
/** German half-sentence naming a code's period of validity, for error texts. */
export function describeCodeValidity(code) {
    if (code.validFrom && code.validUntil) {
        return `gilt nur für Leistungen von ${formatDate(code.validFrom)} bis ${formatDate(code.validUntil)}`;
    }
    if (code.validFrom)
        return `gilt erst für Leistungen ab dem ${formatDate(code.validFrom)}`;
    if (code.validUntil)
        return `galt nur für Leistungen bis zum ${formatDate(code.validUntil)}`;
    return 'gilt ohne zeitliche Einschränkung';
}
//# sourceMappingURL=taxCodes.js.map